import {
    email,
    maxLength,
    minLength,
    parse,
    pipe,
    regex,
    string,
    ValiError
} from 'valibot';

/**
 * Email Validation Schema
 * - Must be a valid email address format
 */
export const emailSchema = pipe(
	string('E-postadress måste vara en text'),
	email('Fyll i en giltig e-postadress.')
)

/**
 * Validate email and return error message if invalid
 */
export const validateEmail = (emailValue: string): { valid: boolean; error?: string } => {
	try {
		parse(emailSchema, emailValue)
		return { valid: true }
	} catch (error) {
		if (error instanceof ValiError) {
			const message = error.issues?.[0]?.message || 'Felaktig e-postadress'
			return { valid: false, error: message }
		}
		return { valid: false, error: 'Felaktig e-postadress' }
	}
}

/**
 * Phone Validation Schema
 * - Must start with + followed by any 2-digit country code (e.g. +46, +45, +47)
 * - Must be exactly 12 characters (e.g. +46708853344)
 * - Only digits after the + and country code
 */
export const phoneSchema = pipe(
	string('Telefonnummer måste vara en text'),
	regex(/^\+\d{2}\d+$/, 'Telefonnumret måste börja med landskod, t.ex. +46'),
	minLength(12, 'Telefonnumret måste vara exakt 12 tecken (t.ex. +46708853344)'),
	maxLength(12, 'Telefonnumret måste vara exakt 12 tecken (t.ex. +46708853344)')
)

/**
 * Validate a phone number and return an error message if invalid.
 * Accepts: +46, +45, or +47 followed by 9 digits — total 12 characters.
 * Example: +46708853344
 */
export const validatePhone = (phoneValue: string): { valid: boolean; error?: string } => {
	try {
		parse(phoneSchema, phoneValue.trim())
		return { valid: true }
	} catch (error) {
		if (error instanceof ValiError) {
			const message = error.issues?.[0]?.message || 'Ogiltigt telefonnummer'
			return { valid: false, error: message }
		}
		return { valid: false, error: 'Ogiltigt telefonnummer' }
	}
}

/**
 * Name Validation (förnamn/efternamn)
 * - Only alphabetic characters including Swedish åäöÅÄÖ, hyphen and space
 * - Max 25 characters
 */
export const nameSchema = pipe(
	string('Namnet måste vara en text'),
	regex(/^[a-zA-ZåäöÅÄÖ\- ]+$/, 'Namnet får endast innehålla bokstäver, bindestreck och mellanslag'),
	maxLength(25, 'Namnet får inte vara längre än 25 tecken')
)

export const validateName = (value: string): { valid: boolean; error?: string } => {
	try {
		parse(nameSchema, value.trim())
		return { valid: true }
	} catch (error) {
		if (error instanceof ValiError) {
			const message = error.issues?.[0]?.message || 'Ogiltigt namn'
			return { valid: false, error: message }
		}
		return { valid: false, error: 'Ogiltigt namn' }
	}
}

/**
 * Swedish Personal Number Validation (personnummer)
 * - Exactly 12 digits, no hyphens
 */
export const personalNumberSchema = pipe(
	string('Personnummer måste vara en text'),
	regex(/^\d{12}$/, 'Personnummer måste bestå av exakt 12 siffror utan bindestreck')
)

export const validatePersonalNumber = (value: string): { valid: boolean; error?: string } => {
	try {
		parse(personalNumberSchema, value.trim())
	} catch (error) {
		if (error instanceof ValiError) {
			const message = error.issues?.[0]?.message || 'Ogiltigt personnummer'
			return { valid: false, error: message }
		}
		return { valid: false, error: 'Ogiltigt personnummer' }
	}

	// Validate date portion (YYYYMMDD) and age constraints
	const v = value.trim();
	const year  = parseInt(v.substring(0, 4), 10);
	const month = parseInt(v.substring(4, 6), 10);
	const day   = parseInt(v.substring(6, 8), 10);

	const birthDate = new Date(year, month - 1, day);
	const isValidDate =
		birthDate.getFullYear() === year &&
		birthDate.getMonth() === month - 1 &&
		birthDate.getDate() === day;

	if (!isValidDate) {
		return { valid: false, error: 'Personnumret innehåller ett ogiltigt datum' };
	}

	const today = new Date();
	const age = today.getFullYear() - year - (
		today.getMonth() < month - 1 ||
		(today.getMonth() === month - 1 && today.getDate() < day) ? 1 : 0
	);

	if (age < 18) {
		return { valid: false, error: 'Användaren måste vara minst 18 år gammal' };
	}
	if (age > 126) {
		return { valid: false, error: 'Personnumret är ogiltigt (födelseår för långt tillbaka)' };
	}

	return { valid: true };
}
